import { resolveQuickAddProperties } from "../cart/quick-add-properties";

const htoCardSelector = '[data-product-card][data-home-try-on="true"]';
const mobileBreakpoint = 768;

const selectSizeLabel = "Select Size";
const addingLabel = "Adding...";
const addedLabel = "Added!";

const minAddingDuration = 900;
const addedDuration = 1500;

const htoCartLimit = 6;

const cartQuantityByProductId = new Map();

function rebuildCartQuantityMap(cart) {
  cartQuantityByProductId.clear();

  (cart?.items || []).forEach((item) => {
    cartQuantityByProductId.set(item.product_id, (cartQuantityByProductId.get(item.product_id) || 0) + item.quantity);
  });
}

fetch("/cart.js")
  .then((response) => response.json())
  .then(rebuildCartQuantityMap)
  .catch((error) => console.error("[HTO] Failed to load initial cart quantities:", error));

document.addEventListener("updateCart", (event) => {
  if (event.detail?.cart?.items) {
    rebuildCartQuantityMap(event.detail.cart);
  }
});

function numericProductIdFromCard(card) {
  const match = card.dataset.productId?.match(/(\d+)$/);

  return match ? Number(match[1]) : null;
}

function isMobile() {
  return window.innerWidth < mobileBreakpoint;
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getSizePanel(card) {
  return card.__sizePanel || card.querySelector("[data-product-card-size-panel]");
}

function detachPanelToBody(panel) {
  if (panel.parentElement === document.body) {
    return;
  }

  panel.__returnParent = panel.parentElement;
  panel.__returnNextSibling = panel.nextSibling;

  document.body.appendChild(panel);
}

function reattachPanel(panel) {
  if (!panel.__returnParent) {
    return;
  }

  if (panel.__returnNextSibling) {
    panel.__returnParent.insertBefore(panel, panel.__returnNextSibling);
  } else {
    panel.__returnParent.appendChild(panel);
  }

  delete panel.__returnParent;
  delete panel.__returnNextSibling;
}

function openSizePanel(card) {
  const panel = getSizePanel(card);
  const trigger = card.querySelector("[data-product-card-select-size]");

  if (!panel || !trigger) {
    return;
  }

  if (isMobile()) {
    document.querySelectorAll(`${htoCardSelector}[data-size-panel-open="true"]`).forEach((openCard) => {
      if (openCard !== card) {
        closeSizePanel(openCard);
      }
    });

    detachPanelToBody(panel);
  }

  panel.setAttribute("aria-hidden", "false");
  trigger.setAttribute("aria-expanded", "true");

  card.dataset.sizePanelOpen = "true";

  if (isMobile()) {
    window.openFullDimmer(card);
    window.lockScroll(card);
  }
}

function closeSizePanel(card) {
  const panel = getSizePanel(card);
  const trigger = card.querySelector("[data-product-card-select-size]");

  if (!panel || !trigger) {
    return;
  }

  panel.setAttribute("aria-hidden", "true");
  trigger.setAttribute("aria-expanded", "false");

  delete card.dataset.sizePanelOpen;

  window.closeFullDimmer(card);
  window.unlockScroll(card);

  if (panel.__returnParent) {
    const restoreOnceClosed = () => {
      panel.removeEventListener("transitionend", restoreOnceClosed);

      if (panel.getAttribute("aria-hidden") !== "true") {
        return;
      }

      reattachPanel(panel);
    };

    panel.addEventListener("transitionend", restoreOnceClosed);
  }
}

document.addEventListener("click", (event) => {
  document.querySelectorAll(`${htoCardSelector}[data-size-panel-open="true"]`).forEach((card) => {
    const panel = getSizePanel(card);

    if (panel && !panel.contains(event.target)) {
      closeSizePanel(card);
    }
  });
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") {
    return;
  }

  document.querySelectorAll(`${htoCardSelector}[data-size-panel-open="true"]`).forEach(closeSizePanel);
});

function setSelectSizeState(card, state) {
  const labelEl = card.querySelector("[data-product-card-select-size-label]");
  const iconEl = card.querySelector("[data-product-card-select-size] .icon--quick-add");

  const labels = { default: selectSizeLabel, adding: addingLabel, added: addedLabel };

  if (labelEl) {
    labelEl.textContent = labels[state] || selectSizeLabel;
  }

  if (iconEl) {
    iconEl.style.display = state === "default" ? "" : "none";
  }
}

function addVariantToCart(variantId, properties) {
  return new Promise((resolve, reject) => {
    if (typeof window.addToCart !== "function") {
      reject(new Error("Global addToCart function is unavailable."));
      return;
    }

    const item = { id: Number(variantId), quantity: 1 };

    if (properties && Object.keys(properties).length) {
      item.properties = properties;
    }

    window.addToCart(item, {
      onError: (response) => {
        console.error("[HTO] Add-to-cart error:", response);

        reject(new Error(response?.description || response?.message || "Unable to add item to cart."));
      },

      onAfterAdd: (response) => {
        resolve(response);
      },
    });
  });
}

function getHtoVariants(card) {
  return Array.from(card.querySelectorAll("[data-hto-variant]")).map((element) => {
    const options = [element.dataset.option1, element.dataset.option2, element.dataset.option3].filter(
      (value) => value !== undefined && value !== null && value !== "",
    );

    return {
      id: element.dataset.variantId,
      available: element.dataset.available === "true",
      options,
    };
  });
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))];
}

function createHtoOptionButton({ value, optionIndex, selected, disabled = false }) {
  const button = document.createElement("button");

  button.type = "button";
  button.className = "product-card__size-option body";

  button.dataset.htoOptionValue = value;
  button.dataset.optionIndex = String(optionIndex);

  button.textContent = value;

  if (selected) {
    button.classList.add("is-selected");
    button.setAttribute("aria-selected", "true");
  }

  if (disabled) {
    button.disabled = true;
    button.classList.add("is-sold-out");
    button.setAttribute("aria-disabled", "true");
  }

  return button;
}

function initializeHtoVariantSelector(card, onVariantResolved) {
  const variants = getHtoVariants(card);
  const groups = Array.from(card.querySelectorAll("[data-hto-option-group]"));

  if (!variants.length || !groups.length) {
    return;
  }

  const selections = new Array(groups.length).fill("");

  function clearSelectionsAfter(index) {
    for (let i = index + 1; i < selections.length; i += 1) {
      selections[i] = "";
    }
  }

  function matchingVariantsBefore(optionIndex) {
    return variants.filter((variant) => {
      for (let i = 0; i < optionIndex; i += 1) {
        if (selections[i] && variant.options[i] !== selections[i]) {
          return false;
        }
      }

      return true;
    });
  }

  function findExactVariant() {
    if (!selections.every(Boolean)) {
      return null;
    }

    return (
      variants.find(
        (variant) => variant.available && selections.every((value, index) => variant.options[index] === value),
      ) || null
    );
  }

  function renderOptions() {
    groups.forEach((group, optionIndex) => {
      const container = group.querySelector("[data-hto-option-values]");

      if (!container) {
        return;
      }

      const matching = matchingVariantsBefore(optionIndex);
      const values = uniqueValues(matching.map((variant) => variant.options[optionIndex]));

      container.innerHTML = "";

      if (!values.length) {
        selections[optionIndex] = "";
        group.hidden = true;

        return;
      }

      const isLastOption = optionIndex === groups.length - 1;

      if (isLastOption && values.length === 1) {
        const singleValue = values[0];
        const hasAvailableVariant = matching.some(
          (variant) => variant.available && variant.options[optionIndex] === singleValue,
        );

        if (hasAvailableVariant) {
          selections[optionIndex] = singleValue;
          group.hidden = true;

          return;
        }
      }

      if (selections[optionIndex] && !values.includes(selections[optionIndex])) {
        selections[optionIndex] = "";
      }

      group.hidden = false;

      values.forEach((value) => {
        const available = matching.some((variant) => variant.available && variant.options[optionIndex] === value);

        const button = createHtoOptionButton({
          value,
          optionIndex,
          selected: selections[optionIndex] === value,
          disabled: !available,
        });

        if (available) {
          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (card.dataset.htoAdding === "true") {
              return;
            }

            selections[optionIndex] = value;

            clearSelectionsAfter(optionIndex);
            renderOptions();

            const variant = findExactVariant();

            if (!variant) {
              return;
            }

            const activeButton = groups[optionIndex].querySelector(`[data-hto-option-value="${CSS.escape(value)}"]`);

            onVariantResolved(variant, [...selections], activeButton || button);
          });
        }

        container.appendChild(button);
      });
    });

    const visibleGroupCount = groups.filter((group) => !group.hidden).length;

    groups.forEach((group) => {
      const label = group.querySelector(".product-card__hto-option-label");

      if (label) {
        label.hidden = visibleGroupCount <= 1;
      }
    });
  }

  card.__resetHtoOptions = () => {
    selections.fill("");
    renderOptions();
  };

  renderOptions();
}

// a data attribute survives cloneNode, and the API-filtered grid clones a card that the global
// bundle already initialised, so every clone arrived marked as done and never got its listeners.
// a WeakSet keys off the element itself, so a clone is always a new, uninitialised element
const initializedHtoCards = new WeakSet();

function initializeHtoCard(card) {
  if (initializedHtoCards.has(card)) {
    return;
  }

  initializedHtoCards.add(card);

  const trigger = card.querySelector("[data-product-card-select-size]");
  const panel = card.querySelector("[data-product-card-size-panel]");

  if (!trigger || !panel) {
    return;
  }

  card.__sizePanel = panel;

  async function addResolvedVariant(variant, selections, selectedButton) {
    if (!variant?.id) {
      return;
    }

    if (trigger.dataset.state === "adding" || trigger.dataset.state === "added") {
      return;
    }

    const productId = numericProductIdFromCard(card);
    const currentQuantity = productId ? cartQuantityByProductId.get(productId) || 0 : 0;

    if (currentQuantity >= htoCartLimit) {
      const message =
        window.localeStrings?.product?.htoLimitReached?.replace("[count]", htoCartLimit) ||
        `You can only add up to ${htoCartLimit} of this item to your bag.`;

      window.showToast(message);

      return;
    }

    card.dataset.htoAdding = "true";

    if (selectedButton) {
      selectedButton.setAttribute("aria-busy", "true");
    }

    trigger.dataset.state = "adding";
    trigger.disabled = true;

    setSelectSizeState(card, "adding");

    try {
      const properties = resolveQuickAddProperties({
        color: card.dataset.color,
        colorCode: card.dataset.colorCode,
        styleGroup: card.dataset.styleGroup,
        deliveryOption: card.dataset.deliveryOption,
      });

      // Matches the suit PDP's properties[fit] (rendered on the card from the
      // same suit-fit-label snippet and 'sections.product.fit' key).
      if (card.dataset.fit && card.dataset.fitProperty) {
        properties[card.dataset.fitProperty] = card.dataset.fit;
      }

      await Promise.all([addVariantToCart(variant.id, properties), wait(minAddingDuration)]);

      trigger.dataset.state = "added";

      setSelectSizeState(card, "added");

      if (isMobile()) {
        const optionLabel = selections.filter(Boolean).join(" / ");

        window.showToast(optionLabel ? `${optionLabel} added to bag` : "Added to bag");
      }

      await wait(addedDuration);

      closeSizePanel(card);
      setSelectSizeState(card, "default");

      trigger.dataset.state = "";
      trigger.disabled = false;

      if (selectedButton) {
        selectedButton.removeAttribute("aria-busy");
      }

      delete card.dataset.htoAdding;

      card.__resetHtoOptions?.();

      document.dispatchEvent(new CustomEvent("mini-cart:open"));
    } catch (error) {
      console.error("[HTO] Add to cart failed:", error);

      setSelectSizeState(card, "default");

      trigger.dataset.state = "";
      trigger.disabled = false;

      if (selectedButton) {
        selectedButton.removeAttribute("aria-busy");
      }

      delete card.dataset.htoAdding;

      window.showToast(error.message || "Unable to add item to bag.");
    }
  }

  initializeHtoVariantSelector(card, addResolvedVariant);

  trigger.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (trigger.dataset.state === "adding" || trigger.dataset.state === "added") {
      return;
    }

    const isOpen = panel.getAttribute("aria-hidden") === "false";

    if (isOpen) {
      closeSizePanel(card);

      return;
    }

    card.__resetHtoOptions?.();
    openSizePanel(card);
  });

  const panelCloseHeader = panel.querySelector("[data-product-card-size-panel-close]");

  panelCloseHeader?.addEventListener("click", (event) => {
    event.preventDefault();

    closeSizePanel(card);
  });

  card.addEventListener("mouseleave", () => {
    if (isMobile()) {
      return;
    }

    if (trigger.dataset.state === "adding" || trigger.dataset.state === "added") {
      return;
    }

    const hasSelection = Boolean(panel.querySelector(".is-selected"));

    if (!hasSelection) {
      closeSizePanel(card);
    }
  });
}

const swatchAddSelector = "[data-product-card-add-to-bag]";

function setAddToBagState(button, state) {
  const labelEl = button.querySelector("[data-product-card-add-to-bag-label]");
  const iconEl = button.querySelector(".icon--quick-add");

  const labels = { adding: addingLabel, added: addedLabel };

  if (labelEl) {
    labelEl.textContent = labels[state] || button.dataset.defaultLabel;
  }

  if (iconEl) {
    iconEl.style.display = state === "default" ? "" : "none";
  }
}

function initializeSwatchAddToBag(card) {
  const button = card.querySelector(swatchAddSelector);

  if (!button || button.__addToBagInitialized) {
    return;
  }

  button.__addToBagInitialized = true;

  const labelEl = button.querySelector("[data-product-card-add-to-bag-label]");

  button.dataset.defaultLabel = labelEl?.textContent.trim() || "";

  button.addEventListener("click", async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (button.dataset.state === "adding" || button.dataset.state === "added") {
      return;
    }

    const variantId = button.dataset.variantId;

    if (!variantId) {
      return;
    }

    button.dataset.state = "adding";
    button.disabled = true;

    setAddToBagState(button, "adding");

    try {
      const properties = resolveQuickAddProperties({
        color: button.dataset.color,
        colorCode: button.dataset.colorCode,
        styleGroup: button.dataset.styleGroup,
        deliveryOption: button.dataset.deliveryOption,
      });

      await Promise.all([addVariantToCart(variantId, properties), wait(minAddingDuration)]);

      button.dataset.state = "added";

      setAddToBagState(button, "added");

      await wait(addedDuration);

      document.dispatchEvent(new CustomEvent("mini-cart:open"));
    } catch (error) {
      console.error("[PLP] Quick add to bag failed:", error);

      window.showToast(error.message || "Unable to add item to bag.");
    } finally {
      button.dataset.state = "";
      button.disabled = false;

      setAddToBagState(button, "default");
    }
  });
}

const wishlistSelector = "[data-add-to-wishlist]";

function setWishlistButtonActive(button, active) {
  const defaultIcon = button.querySelector("[data-wishlist-icon-default]");
  const filledIcon = button.querySelector("[data-wishlist-icon-filled]");

  button.setAttribute("aria-pressed", String(active));
  button.classList.toggle("is-active", active);

  if (defaultIcon) {
    defaultIcon.hidden = active;
  }

  if (filledIcon) {
    filledIcon.hidden = !active;
  }
}

// The saved list only lives in Swym, so a card cannot know on its own whether its product is
// already in it. The list is fetched once per page and shared by every card.
let savedProductIdsPromise = null;

function getSavedProductIds() {
  if (savedProductIdsPromise) return savedProductIdsPromise;

  savedProductIdsPromise = new Promise((resolve) => {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      swat.fetchLists({
        callbackFn: (lists) => {
          const ids = new Set();

          (lists || []).forEach((list) => {
            (list.listcontents || []).forEach((item) => ids.add(String(item.empi)));
          });

          resolve(ids);
        },
        errorFn: () => resolve(new Set()),
      });
    });
  });

  return savedProductIdsPromise;
}

function markSavedWishlistButton(button) {
  const productId = button.dataset.wishlistProductId;

  if (!productId) return;

  getSavedProductIds().then((ids) => setWishlistButtonActive(button, ids.has(String(productId))));
}

// a save or removal anywhere on the page invalidates the cached list
document.addEventListener("wishlist:updated", () => {
  savedProductIdsPromise = null;
  document.querySelectorAll(wishlistSelector).forEach(markSavedWishlistButton);
});

const initializedWishlistButtons = new WeakSet();

function initializeWishlistButton(button) {
  if (initializedWishlistButtons.has(button)) {
    return;
  }

  initializedWishlistButtons.add(button);

  markSavedWishlistButton(button);

  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    const card = button.closest("[data-product-card]");

    if (!card) {
      return;
    }

    const imageEl = card.querySelector("[data-product-card-image] img");
    const badgeEls = card.querySelectorAll("[data-product-card-badges] .badge");

    const detail = {
      onAdd: () => setWishlistButtonActive(button, true),
      productId: card.dataset.productId,
      productGroupId: card.dataset.productGroupId || "",
      color: card.dataset.color || "",
      image: imageEl?.currentSrc || imageEl?.src || "",
      imageAlt: imageEl?.alt || "",
      badges: Array.from(badgeEls).map((el) => el.textContent.trim()),
      swymProductId: button.dataset.wishlistProductId || "",
      swymVariantId: button.dataset.wishlistVariantId || "",
      swymProductUrl: button.dataset.wishlistProductUrl || "",
    };

    document.dispatchEvent(new CustomEvent("drawer:before-open", { detail: { name: "wishlist" } }));
    document.dispatchEvent(new CustomEvent("wishlist:open", { detail }));
  });
}

const showroomSelector = "[data-add-to-showroom]";

const initializedShowroomButtons = new WeakSet();

function initializeShowroomButton(button) {
  if (initializedShowroomButtons.has(button)) {
    return;
  }

  initializedShowroomButtons.add(button);

  const defaultIcon = button.querySelector("[data-showroom-icon-default]");
  const filledIcon = button.querySelector("[data-showroom-icon-filled]");

  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isActive = button.getAttribute("aria-pressed") === "true";
    const nextActive = !isActive;

    button.setAttribute("aria-pressed", String(nextActive));
    button.classList.toggle("is-active", nextActive);

    if (defaultIcon) {
      defaultIcon.hidden = !nextActive;
    }

    if (filledIcon) {
      filledIcon.hidden = nextActive;
    }
  });
}

function initializeProductCardInteractions(root = document) {
  root.querySelectorAll(htoCardSelector).forEach(initializeHtoCard);

  root.querySelectorAll(swatchAddSelector).forEach((button) => {
    initializeSwatchAddToBag(button.closest("[data-product-card]"));
  });

  root.querySelectorAll(wishlistSelector).forEach(initializeWishlistButton);

  root.querySelectorAll(showroomSelector).forEach(initializeShowroomButton);
}

window.initializeProductCardInteractions = initializeProductCardInteractions;

initializeProductCardInteractions();
